/**
 * LogRocket privacy settings, shared by every app that records sessions
 * (Admin Hub, agency portal, mobile web, mobile native).
 *
 * Owner's decision (Nir, 2026-10-22, replacing the 2026-10-01 one): keep
 * LogRocket, send it no patient data, and accept that replays show masked text.
 * So nothing a person typed, read or uploaded leaves the device:
 *   - Network: a request keeps its method, status, timing, origin and path. The
 *     query string and fragment are cut off, any path part that is not a plain
 *     route word or a numeric id is replaced (names, phone numbers, emails,
 *     search terms and link tokens all look like that), request and response
 *     bodies are never sent, and only a short list of harmless headers is kept
 *     (`x-request-id` among them, the bridge from a replay to CloudWatch).
 *   - Who: a session is identified by the internal user id and role only.
 *   - Screen (web): every text node, input and image is masked, page titles are
 *     not recorded, attributes that carry a URL or a whole document (`src`,
 *     `srcdoc`, ...) are dropped, and links that leave the app or carry a query
 *     (`tel:`, `mailto:`, a signed S3 link) are recorded as blank boxes. The
 *     native app has its own screen options (`apps/mobile/lib/logrocket.ts`).
 *   - Console: not recorded. Log lines are free text and cannot be sanitized.
 * What a replay still shows: clicks and taps, page paths, scrolling, the shape
 * of every screen (with masked text), request timing and status, and errors.
 *
 * Plain functions and constants with no SDK import, so they work with the web
 * SDK (`logrocket`) and `@logrocket/react-native` alike; both hand the
 * sanitizers `{ reqId, url, headers, body, method, ... }`.
 *
 * LogRocket docs (checked 2026-10-22):
 *   https://docs.logrocket.com/reference/dom      (text/input/image sanitizers,
 *                                                  redactSelectors, hiddenAttributes)
 *   https://docs.logrocket.com/reference/network  (request/response sanitizers)
 *   https://docs.logrocket.com/reference/browser  (urlSanitizer)
 *   https://docs.logrocket.com/reference/console  (console.isEnabled)
 *   https://docs.logrocket.com/reference/identify (uid and traits)
 */
/** What a removed value is replaced with. */
export const REDACTED = "[redacted]";
/**
 * The prefix a masked value keeps in front of its last 4 characters. LogRocket
 * no longer uses it (bodies are dropped whole); the admin audit log
 * (`packages/api/src/services/audit-redaction.ts`) still masks bank account and
 * social security numbers with it.
 */
export const MASK = "••••";
// ─── URLs ────────────────────────────────────────────────────────────────────
/**
 * A numeric id: a visit, nurse, agency or ticket id. Ten digits and more is a
 * phone number, not an id, so it does not count.
 */
const NUMERIC_ID = /^\d{1,9}$/;
/**
 * One piece of a route word: letters, a short number, or letters and a short
 * number side by side (`v485`, `2fa`, `s3`). A random token mixes letters and
 * digits throughout, so it fails here.
 */
const WORD_PART = "(?:[a-z]+|\\d{1,4}|[a-z]+\\d{1,4}|\\d{1,4}[a-z]+)";
/**
 * A route word: lowercase pieces joined by `-` or `_`, optionally led by `_`
 * (Next.js `_next`). Matches `api`, `audio-upload-url`, `cms485-signed`.
 * Uppercase, `.`, `@`, `+`, `%`, spaces and long digit runs all fail.
 */
const ROUTE_WORD = new RegExp(`^_?${WORD_PART}(?:[-_]${WORD_PART})*$`);
/**
 * Longer than any route word we use. The link tokens in our paths are 43 to 64
 * characters (`/v485/:token`, `/q/:token`, `/references/:token`).
 */
const MAX_ROUTE_WORD_LENGTH = 40;
/**
 * More digits than any route word has (`cms485-signed` has 3). Catches a phone
 * number or a date written with dashes (`555-010-0123`, `1950-01-02`), whose
 * pieces are each short enough to pass `WORD_PART`.
 */
const MAX_ROUTE_WORD_DIGITS = 4;
/** `scheme:` at the start of a URL. */
const SCHEME = /^([a-z][a-z0-9+.-]*):/i;
/**
 * Whether a path segment can stay as it is. Everything else (a name, a phone
 * number, an email, a file name, a UUID, a link token) becomes `[redacted]`.
 * A plain lowercase word is kept, so this relies on no route of ours putting a
 * person's name in its path as one lowercase word (none does, 2026-10-22: route
 * params are ids, tokens, slugs and enum keys).
 */
function isKeptSegment(segment) {
    if (segment === "" || NUMERIC_ID.test(segment)) {
        return true;
    }
    return (segment.length <= MAX_ROUTE_WORD_LENGTH &&
        ROUTE_WORD.test(segment) &&
        (segment.match(/\d/g)?.length ?? 0) <= MAX_ROUTE_WORD_DIGITS);
}
function sanitizePath(path) {
    return path
        .split("/")
        .map((segment) => (isKeptSegment(segment) ? segment : REDACTED))
        .join("/");
}
/** `user:pass@host:port` → `host:port`, lowercased. */
function sanitizeAuthority(authority) {
    return authority.slice(authority.lastIndexOf("@") + 1).toLowerCase();
}
/** `//authority/path` → `//host/sanitized-path`. */
function sanitizeNetworkPath(rest) {
    const pathStart = rest.indexOf("/", 2);
    const authority = pathStart === -1 ? rest.slice(2) : rest.slice(2, pathStart);
    const path = pathStart === -1 ? "" : rest.slice(pathStart);
    return `//${sanitizeAuthority(authority)}${sanitizePath(path)}`;
}
/**
 * Cuts a URL down to what LogRocket may keep: scheme, host and port, and the
 * path with every segment that is not a route word or a numeric id replaced.
 * The query string, the fragment and any `user:password@` are dropped.
 *
 *   https://api2.taskshealth.com/api/agency/cases?search=Jane
 *     → https://api2.taskshealth.com/api/agency/cases
 *   /v485/<43-character link token>/doc → /v485/[redacted]/doc
 *   /api/admin/visits/420041/wake → unchanged
 *   tel:+15550100 → tel:[redacted]
 *
 * Used for page URLs (`browser.urlSanitizer`) and network URLs alike. Works
 * on absolute, protocol-relative and relative URLs, and never throws.
 */
export function sanitizeLogRocketUrl(url) {
    // A URL object (or anything else) is read as its string form.
    const text = typeof url === "string" ? url : String(url ?? "");
    // Everything from the first `?` or `#` on: search terms, names, phone
    // numbers, emails and tokens all travel there.
    const head = text.split(/[?#]/, 1)[0] ?? "";
    const scheme = SCHEME.exec(head);
    if (scheme) {
        const rest = head.slice(scheme[0].length);
        const name = (scheme[1] ?? "").toLowerCase();
        // `data:`, `blob:`, `tel:`, `mailto:`, `javascript:` ...: everything
        // after the scheme is the payload itself.
        return rest.startsWith("//")
            ? `${name}:${sanitizeNetworkPath(rest)}`
            : `${name}:${REDACTED}`;
    }
    if (head.startsWith("//")) {
        return sanitizeNetworkPath(head);
    }
    return sanitizePath(head);
}
/** A missing URL stays missing; anything else is cut down. */
function sanitizeUrlField(value) {
    return value === null || value === undefined ? value : sanitizeLogRocketUrl(value);
}
// ─── Network ─────────────────────────────────────────────────────────────────
/**
 * The only headers a recorded request or response keeps (lowercase names).
 * Everything else is dropped: `Authorization`, cookies, API keys, `Location`
 * (a redirect URL with its query), and any custom header we add later.
 */
const KEPT_HEADERS = new Set([
    "accept",
    "cache-control",
    "content-encoding",
    "content-length",
    "content-type",
    "date",
    "retry-after",
    "x-app-platform",
    "x-app-version",
    "x-cache",
    "x-ratelimit-limit",
    "x-ratelimit-remaining",
    "x-ratelimit-reset",
    "x-request-id"
]);
function keepSafeHeaders(headers) {
    const out = {};
    for (const [name, value] of Object.entries(headers)) {
        if (KEPT_HEADERS.has(name.toLowerCase())) {
            out[name] = value;
        }
    }
    return out;
}
function isPlainObject(value) {
    if (value === null || typeof value !== "object") {
        return false;
    }
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
function sanitizeEntry(entry) {
    if (!isPlainObject(entry)) {
        return entry;
    }
    // reqId, method, status, mode, credentials, initiatorType pass through:
    // they are what makes a request findable and timeable in a replay.
    const out = { ...entry };
    if ("url" in entry) {
        out.url = sanitizeUrlField(entry.url);
    }
    if ("referrer" in entry) {
        out.referrer = sanitizeUrlField(entry.referrer);
    }
    if ("headers" in entry) {
        out.headers = isPlainObject(entry.headers)
            ? keepSafeHeaders(entry.headers)
            : {};
    }
    // Never sent, whatever it is. `undefined` (LogRocket's own example for a
    // response) rather than null, so both SDKs record "no body".
    out.body = undefined;
    return out;
}
/**
 * `network.requestSanitizer`. Returns a copy with the body dropped, the URL and
 * referrer cut down by `sanitizeLogRocketUrl` and only `KEPT_HEADERS` left.
 * Never returns null: LogRocket would then drop the request from the replay
 * (and, on the web, the timing of every image and script with it).
 */
export function sanitizeLogRocketRequest(request) {
    return sanitizeEntry(request);
}
/** `network.responseSanitizer`. Same rules as requests; the status code stays. */
export function sanitizeLogRocketResponse(response) {
    return sanitizeEntry(response);
}
/**
 * The internal user id as the LogRocket uid, plus the role and (for an agency
 * member) the agency and membership ids. Picks those fields one by one, so a
 * full user object with a name, email or phone can be passed in and none of it
 * reaches LogRocket. No `name` or `email` trait either: LogRocket shows those
 * in place of the uid in its UI.
 */
export function logRocketIdentity(user) {
    const traits = { role: String(user.role) };
    if (typeof user.agencyId === "number") {
        traits.agencyId = user.agencyId;
    }
    if (typeof user.agencyMemberId === "number") {
        traits.agencyMemberId = user.agencyMemberId;
    }
    return { uid: String(user.id), traits };
}
// ─── Web init options ────────────────────────────────────────────────────────
/**
 * Attributes never recorded on any element (`dom.hiddenAttributes`). Each one
 * can hold a URL to a patient file (a signed S3 link: `src` on audio/video and
 * `<source>`, `srcset`, `poster`, `<object data>`), a file name (`download`), or
 * a whole document (`srcdoc`: the nurse PWA shows visit documents in an
 * `<iframe srcdoc>`). `src` on `<img>` is already replaced by the image
 * sanitizer, and LogRocket never records `<iframe src>`. `href` cannot go here:
 * replays load the app's stylesheets from `<link href>`; links are handled by
 * `LOGROCKET_REDACT_SELECTORS` instead.
 */
export const LOGROCKET_HIDDEN_ATTRIBUTES = [
    "src",
    "srcset",
    "srcdoc",
    "poster",
    "data",
    "download"
];
/**
 * Elements recorded as a blank box of the same size, with no attributes that
 * matter and none of their children (`dom.redactSelectors`, like
 * `data-private`):
 *   - every link whose `href` is not an in-app path: `tel:`, `sms:`, `mailto:`,
 *     `blob:`, an `https://` link to a signed S3 file or a map, `#...`;
 *   - every link with a query string, in-app ones included;
 *   - frames and embeds, whose content can be a document.
 */
export const LOGROCKET_REDACT_SELECTORS = [
    'a[href]:not([href^="/"])',
    'a[href^="//"]',
    'a[href*="?"]',
    "iframe",
    "object",
    "embed"
];
/**
 * `LogRocket.init` options for the web SDK (agency portal, Admin Hub, nurse
 * PWA). A fresh object each call, so no app can change another's settings.
 */
export function logRocketWebOptions() {
    return {
        dom: {
            // Every text node, plus alt, title and aria-label attributes.
            textSanitizer: true,
            // Every <input>, <textarea> and <select>.
            inputSanitizer: true,
            // Every <img>: the source is replaced, so a patient photo, a map
            // tile or a signature image never reaches LogRocket.
            imageSanitizer: true,
            // document.title, which can name the record on screen.
            disablePageTitles: true,
            hiddenAttributes: [...LOGROCKET_HIDDEN_ATTRIBUTES],
            redactSelectors: [...LOGROCKET_REDACT_SELECTORS]
        },
        network: {
            requestSanitizer: sanitizeLogRocketRequest,
            responseSanitizer: sanitizeLogRocketResponse
        },
        browser: {
            urlSanitizer: sanitizeLogRocketUrl
        },
        console: {
            isEnabled: false
        }
    };
}
//# sourceMappingURL=logrocket-sanitize.js.map